import { mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  EntrancePlantDetailDTO,
  GateGuestDTO,
  TimeSlot,
  WeekDay,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import ContactTab from '../GuestDetailsEdit/ContactTab/ContactTab.vue';
import GateAccessTab from '../GuestDetailsEdit/GateAccessTab/GateAccessTab.vue';
import { TimeSlotWithValidation } from '../GuestDetailsEdit/GuestDetailsEdit';
import BadgeTab from '../GuestDetailsEdit/BadgeTab/BadgeTab.vue';
import CodeTab from '../GuestDetailsEdit/CodeTab/CodeTab.vue';

@Options({
  name: 'DialogAddGuest',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    isOneTime: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initGuest();
        this.initCheckEdit();
      }
    },
  },
  components: { ContactTab, GateAccessTab, BadgeTab, CodeTab },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddGuest extends Vue {
  newGuest: GateGuestDTO = new GateGuestDTO();
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisible!: boolean;
  isOneTime!: boolean;
  isDialogVisible = false;

  timesSlots: TimeSlotWithValidation[] = [];
  guestContactEdited = false;
  guestGateAccessEdited = false;
  guestBadgeEdited = false;
  guestKeyCodeEdited = false;
  errorContact = false;
  errorGateAccess = false;
  errorBadge = false;
  errorKeyCode = false;

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  get tomorrowDate() {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }

  initGuest() {
    this.newGuest.description = '';
    this.newGuest.isTimeAlways = true;
    this.newGuest.timesSlots = [];
    this.newGuest.gateIds = [];
    this.newGuest.keypadCode = '';

    if (!this.isOneTime) {
      this.newRecurrentGuest(this.tomorrowDate);
    } else {
      this.newOneTimeGuest(this.tomorrowDate);
    }
    this.timesSlots = [];
  }

  newRecurrentGuest(since: Date) {
    const startDate = new Date(
      Date.UTC(since.getFullYear(), since.getMonth(), since.getDate())
    );
    this.newGuest.isRecurrent = true;
    this.newGuest.isOneTimeGuest = false;
    this.newGuest.isDateAlways = true;
    this.newGuest.durationSince = startDate;
    this.newGuest.durationUntil = new Date(startDate);
    this.newGuest.repetitionDays = [
      WeekDay.Monday,
      WeekDay.Tuesday,
      WeekDay.Wednesday,
      WeekDay.Thursday,
      WeekDay.Friday,
      WeekDay.Saturday,
      WeekDay.Sunday,
    ];
  }

  newOneTimeGuest(until: Date) {
    this.newGuest.isRecurrent = false;
    this.newGuest.isOneTimeGuest = true;
    this.newGuest.durationSince = until;
    this.newGuest.durationUntil = until;
    this.newGuest.repetitionDays = [];
  }

  initCheckEdit() {
    this.guestContactEdited = false;
    this.guestGateAccessEdited = false;
    this.guestBadgeEdited = false;
    this.guestKeyCodeEdited = false;
    this.errorContact = false;
    this.errorGateAccess = false;
    this.errorBadge = false;
    this.errorKeyCode = false;
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isOneTime
      ? this.$t('AppResourceEP.CreateOneTimeGuests')
      : this.$t('AppResourceEP.CreatePeriodicGuests');
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get saveButtonEnable() {
    const tabEdited =
      this.guestContactEdited ||
      this.guestGateAccessEdited ||
      this.guestBadgeEdited ||
      this.guestKeyCodeEdited;

    const tabHasError =
      this.errorContact ||
      this.errorGateAccess ||
      this.errorBadge ||
      this.errorKeyCode;

    const essentialsInfo = this.checkEssentialsInfo();
    return essentialsInfo && tabEdited && !tabHasError;
  }

  checkEssentialsInfo(): boolean {
    const checkContact = this.newGuest.description?.trim() !== '';

    const checkGateAccess = this.newGuest.gateIds?.length !== 0;

    return checkContact && checkGateAccess;
  }

  handleContactEdited(value: boolean) {
    this.guestContactEdited = value;
  }

  handleGateAccesEdited(value: boolean) {
    this.guestGateAccessEdited = value;
  }

  handleBadgeEdited(value: boolean) {
    this.guestBadgeEdited = value;
  }

  handleKeyCodeEdited(value: boolean, keyCode: string) {
    this.guestKeyCodeEdited = value;
    if (!this.errorKeyCode) {
      this.newGuest.keypadCode = keyCode;
    }
  }

  handleErrorContact(value: boolean) {
    this.errorContact = value;
  }

  handleErrorGateAccess(value: boolean) {
    this.errorGateAccess = value;
  }

  handleErrorBadge(value: boolean) {
    this.errorBadge = value;
  }

  handleErrorKeyCode(value: boolean) {
    this.errorKeyCode = value;
  }

  retrieveEditedTimesSlots() {
    const retrievedTimesSlots: TimeSlot[] = [];
    this.timesSlots.every((editedTime) =>
      retrievedTimesSlots.push(editedTime.slot)
    );
    return retrievedTimesSlots;
  }

  async handleButtonSubmitAction() {
    this.newGuest.timesSlots = this.retrieveEditedTimesSlots();
    this.isDialogVisible = false;

    await this.saveGuest();

    this.$emit('update-list');
  }

  async saveGuest() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.newGuest = await plantsClient.guestPOST(
        this.currentEntrancePlant.desktopId,
        this.newGuest
      );

      this.$toast.add({
        severity: 'success',
        summary: this.$t('entrancePlant.gateGuestBannerCreated'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
